import React, { useContext, useEffect, useState } from 'react';
import { toCurrency, toFormattedDate } from '@app/utilities';
import { useAsyncEffect } from '@app/hooks';
import { AccountContext } from '../../AccountContext';
import DueAllocations from './DueAllocations';
import LedgerAllocations from './LedgerAllocations';
import './AllocatedTransactions.css';
import MatchedRefundTransaction from './MatchedRefundTransaction';
import ReverseTransaction from './ReverseTransaction';
import CreditAllocationDetails from './CreditAllocationDetails';
import { useCurrentBillingPeriod } from '@app/sdk';
import { CopyToClipboardIcon } from '../../../../shared/CopyToClipboardIcon';

export default function AllocatedTransactions() {
  const { accountId, searchService, selectedBillingPeriod, setError } = useContext(AccountContext);
  const [allocatedTransactions, setAllocatedTransactions] = useState([]);
  const [loading, setLoading] = useState(true as boolean);
  const [selectedTransaction, setSelectedTransaction] = useState();
  const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);

  useEffect(() => {
    setLoading(true);
    setAllocatedTransactions([]);
  }, [selectedBillingPeriod]);

  useAsyncEffect(async () => {
    try {
      const response = await searchService.getTransactionsForBillingPeriod(accountId, 
        selectedBillingPeriod.billingPeriodId != currentBillingPeriod.billingPeriodId ? selectedBillingPeriod.billingPeriodId : -1,
        50,
        0)
    
      setAllocatedTransactions(response.value.reverse());
      setLoading(false);
    } catch (error) {
      setError({ source: 'AllocatedTransactions', ...error });
      setLoading(false);
    }
  }, [accountId, selectedBillingPeriod]);

  return (
    <>
      <h2 className="ui header">Transactions</h2>
      {loading && <div className="ui active centered inline loader" />}
      {!loading && allocatedTransactions.length == 0 && <p>No transactions</p>}

      {!loading && allocatedTransactions.length > 0 && (
        <div className="scrollable">
          <table className="ui single line table">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>Amount</th>
                <th>Effective Date</th>
                <th>Posting Date</th>
                <th>Ledger Allocations</th>
                <th>Due Allocations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocatedTransactions.map((a) => (
                <React.Fragment key={a.transactionId}>
                  <tr>
                    <td>
                      {a.balanceMovementType} ({a.description})
                      {a.upfrontPaymentAmount && <div className="ui grey sub header">Upfront payment amount: {toCurrency(a.upfrontPaymentAmount.value)}</div>}
                      <div className="ui grey sub header" style={{ marginTop: 0 }}>{a.transactionId}<CopyToClipboardIcon value={a.transactionId} /></div>
                    </td>
                    <td>{toCurrency(a.amount.value)}</td>
                    <td>{toFormattedDate(a.effectiveDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>{toFormattedDate(a.postingDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>
                      <LedgerAllocations transactionId={a.transactionId} />
                    </td>
                    <td>{a.balanceMovementType == 'Payment' && <DueAllocations transactionId={a.transactionId} />}</td>
                    <td className="collapsing">
                      <div className="ui actions">
                        {a.balanceMovementType == 'Purchase' && (
                          <MatchedRefundTransaction
                            key={`clear-${a.transactionId}`}
                            description={a.description}
                            transactionId={a.transactionId}
                            initialAmount={a.amount.value * -1}
                            initialUpfrontPaymentAmount={a.upfrontPaymentAmount == null ? undefined : Math.abs(a.upfrontPaymentAmount.value)}
                          />
                        )}
                        <div className="field fill">
                          {(a.balanceMovementType == 'Payment' ||
                            a.balanceMovementType == 'Interest' ||
                            a.balanceMovementType == 'Fee') && (
                            <ReverseTransaction
                              description={a.description}
                              transactionId={a.transactionId}
                              balanceMovementType={a.balanceMovementType}
                            />
                          )}
                          {a.direction == "Credit" && (
                            <div className="ui labeled input">
                              <button className="ui right floated button" onClick={() => setSelectedTransaction(a)}>
                                Allocations
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {<CreditAllocationDetails transaction={selectedTransaction} />}
    </>
  );
}
